//
//


import { mapMutations, mapGetters, mapActions } from "vuex";
import { required, minLength } from "vuelidate/src/validators";
import { $http, readCookie, eraseCookie } from "@/assets/js/appFunc";
import MultiLanguage from "@/components/Login/MultiLanguage.vue";
import AQInput from "@/components/Generics/AQInput/AQInput.vue";
import AQButton from "@/components/Generics/AQButton/AQButton.vue";
import AQAvatar from "@/components/Generics/AQAvatar/AQAvatar.vue";
import { PassValidator } from "@/components/Login/PassValidator";
import { PLATFORM_BASE_PATH } from "@/assets/js/appConfig";

import SetAccessibility from "./SetAccessibility.vue";

export default {
    components: {
        AQInput,
        AQButton,
        AQAvatar,
        SetAccessibility,
        MultiLanguage
    },
    beforeRouteEnter(to, from, next) {
        next(vueInstance => {
            const vm = vueInstance;
            if (to.params && to.params.initialState) {
                vm.emailState = true;
                vm.username = to.params.username;
            }
        });
    },
    beforeRouteUpdate(to, from, next) {
        if (from.name === to.name && from.path !== to.path) {
            this.$root.$emit("backToMaintenance");
        }
        next();
    },
    props: {
        pid: { type: String, default: "" },
        signUpAppId: { type: String, default: "" },
        accessibilityEnabled: { type: Boolean, default: false }
    },
    data() {
        return {
            inviteUserDetails: {
                password: ""
            },
            passwordsMatchErr: false,
            createUserId: "",
            password: "",
            username: "",
            userInvalidCredentials: "",
            otherErrorMsg: "",
            passwordFieldType: "password",
            checkMobile: false,
            emailState: true,
            emailEmpty: false,
            notMaintenanceUser: false,
            passEmpty: false,
            userStatusError: false,
            wrongEmailPass: false,
            otherError: false,
            isLogout: false,
            hideReset: false,
            showPassForSAML: false,
            defaultLanguage: "en",
            firstNameEmpty: false,
            lastNameEmpty: false,
            showSignUpCard: false,
            confirmSignUpCard: false,
            signUpError: false,
            emailSignup: "",
            closeAccessibility: false
        };
    },
    validations: {
        inviteUserDetails: {
            password: {
                minLength: minLength(8),
                required,
                PassValidator
            },
            confirmPassword: {
                required,
                matchPass(v) {
                    return v === this.inviteUserDetails.password;
                }
            },
            firstName: {
                required
            },
            lastName: {
                required
            }
        }
    },
    computed: {
        ...mapGetters("main", ["getShowMultiLangOption", "getShowAccessibilityOption", "getFromAccountDisabled"]),
        emailValidator() {
            let errorMessage = "";
            if (this.emailEmpty) {
                errorMessage = this.$t("login.emptyUser");
            } else if (this.notMaintenanceUser) {
                errorMessage = this.$t("login.errMaintenance");
            }
            return errorMessage;
        },
        signUpValidator() {
            return this.$t("login.invalidSignUpEmail");
        },
        isError() {
            return this.userStatusError || this.wrongEmailPass || this.otherError;
        },
        errorMsgComp() {
            let msgToShow = this.otherErrorMsg;
            if (this.userStatusError) {
                msgToShow = this.$t("login.invalidUser");
            } else if (this.wrongEmailPass) {
                msgToShow = this.userInvalidCredentials;
            }
            return msgToShow;
        },
        passValidator() {
            return this.passEmpty ? this.$t("login.emptyPass") : "";
        },
        displayAccessibility() {
            return this.getShowAccessibilityOption && !this.isLogout && !this.confirmSignUpCard;
        },
        invitePasswordValidator() {
            let errorMessage = "";
            if (!this.$v.inviteUserDetails.password.required || !this.$v.inviteUserDetails.password.minLength
            || !this.$v.inviteUserDetails.password.PassValidator) {
                errorMessage = this.$t("admin.usersAndRoles.manualAddModal.passwordRequirements");
            }
            return errorMessage;
        },
        checkFirstName() {
            return this.firstNameEmpty ? this.$t("login.emptyFirstName") : "";
        },
        checkLastName() {
            return this.lastNameEmpty ? this.$t("login.emptyLastName") : "";
        },
        checkPass() {
            let errorMessage = "";
            if (!this.$v.inviteUserDetails.confirmPassword.required) {
                errorMessage = this.$t("login.emptyPass");
            } else if (!this.$v.inviteUserDetails.confirmPassword.matchPass) {
                errorMessage = this.$t("login.matchPassErr");
            }
            return errorMessage;
        }
    },
    created() {
        this.createUserId = localStorage.getItem("createUserId");
        if (this.createUserId) {
            this.getInvitedUserInfo(this.createUserId).then(data => {
                if (data && data.data) this.inviteUserDetails = { ...data.data };
            });
        }
        window.addEventListener("beforeunload", localStorage.removeItem("createUserId"));
    },
    mounted() {
        const lastUserLogged = localStorage.getItem("lastUserLogged");
        const localLogout = localStorage.getItem("isLogout");
        this.checkNotifSession();
        const size = window.innerWidth;
        this.checkMobile = size < this.$q.screen.sizes.sm;
        if (localLogout) {
            this.isLogout = true;
            localStorage.setItem("changedFlag", false);
            localStorage.setItem("changedAccessibility", false);
        } else if (lastUserLogged) {
            this.username = lastUserLogged;
            const isFromSAML = JSON.parse(localStorage.getItem("fromSAML"));
            const isUserUSR = JSON.parse(localStorage.getItem("isUserUsr"));
            if (this.getFromAccountDisabled) {
                this.$store.commit("main/setFromAccountDisabled", false);
            } else if (isUserUSR || isFromSAML) {
                this.checkEmail();
            }
            this.emailState = isUserUSR ? !isUserUSR : !isFromSAML;
            const maintenanceStarted = localStorage.getItem("maintenanceStarted");
            if (maintenanceStarted === "true") {
                this.emailState = true;
            }
        }
        localStorage.removeItem("isLogout");
    },
    methods: {
        ...mapActions("main", ["getMultiFeaturesOption"]),
        ...mapActions("admin", ["setupNewUser", "getInvitedUserInfo", "twoFactorCode", "getUserWallet"]),
        ...mapMutations("main", ["setUserLogin"]),
        ...mapActions("tasks", ["getUserDetails"]),
        beforeLogin(invitedUser) {
            if (this.password || typeof invitedUser === "boolean") {
                this.$checkCaptcha().then(token => this.login(token, invitedUser));
            } else {
                this.passEmpty = true;
            }
        },
        checkNotifSession() {
            const sessionExpired = sessionStorage.getItem("sessionExpired");
            if (sessionExpired) {
                sessionStorage.removeItem("sessionExpired");
                this.$toast.showNotify({
                    type: "error",
                    message: this.$t("admin.authentication.logoutSAMLError")
                });
            }
        },
        checkLanguage() {
            let language = null;
            if (this.getShowMultiLangOption) {
                if (localStorage.changedFlag === "true") {
                    language = localStorage.language;
                }
            } else {
                language = this.defaultLanguage;
            }
            return language;
        },
        checkAccessibility() {
            let accessibility = null;
            if (this.getShowAccessibilityOption) {
                if (localStorage.changedAccessibility === "true") {
                    accessibility = localStorage.accessibility;
                }
            }
            return accessibility ? accessibility === "true" : accessibility;
        },
        userSignUp() {
            if (this.signUpAppId) {
                this.showSignUpCard = true;
                this.closeAccessibility = true;
            }
        },
        changeAccessibility(event) {
            this.closeAccessibility = event;
        },
        startSignUpApp() {
            if (this.emailSignup) {
                const regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
                this.signUpError = !regex.test(this.emailSignup);
            } else {
                this.signUpError = true;
            }
            if (!this.signUpError) {
                const applicationProps = {
                    appId: this.signUpAppId,
                    emailSignUp: this.emailSignup
                };
                this.$store.dispatch("apps/startApp", applicationProps).then(() => {
                    this.showSignUpCard = false;
                    this.confirmSignUpCard = true;
                });
            }
        },
        actionToLogin() {
            this.confirmSignUpCard = false;
            this.showSignUpCard = false;
            this.emailSignup = "";
            this.closeAccessibility = true;
            this.removeSignUpErrors();
        },
        removeSignUpErrors() {
            this.signUpError = false;
        },
        createAccount() {
            this.$v.$touch();
            this.firstNameEmpty = !this.inviteUserDetails.firstName.trim();
            this.lastNameEmpty = !this.inviteUserDetails.lastName.trim();
            if (!this.$v.$invalid) {
                const payload = { ...this.inviteUserDetails, userId: this.createUserId };
                if (!this.firstNameEmpty && !this.lastNameEmpty && !this.passEmpty && !this.passwordsMatchErr) {
                    this.setupNewUser(payload).then(() => {
                        localStorage.removeItem("createUserId");
                        if (this.inviteUserDetails.isTwoFactor) {
                            this.createUserId = false;
                            this.emailState = true;
                            this.username = this.inviteUserDetails.email;
                        } else {
                            this.beforeLogin(true);
                        }
                    })
                        .catch(error => {
                            if (error.response.status === 400) {
                                localStorage.removeItem("createUserId");
                            }
                        });
                }
            }
        },
        login(captchaToken, invitedUser) {
            localStorage.removeItem("isMyTeamDashboard");
            this.getMultiFeaturesOption().then(data => {
                const accessibilityObj = data.data.find(item => item.code === "ACCESSIBILITY");
                localStorage.setItem("accessibilityFeature", accessibilityObj.enabled);
            });
            $http({
                url: "/auth/login/basic",
                data: {
                    captchaToken,
                    tenantDomain: "default",
                    username: typeof invitedUser === "boolean" ? this.inviteUserDetails.email : this.username.toLowerCase(),
                    userPassword: typeof invitedUser === "boolean" ? this.inviteUserDetails.password : this.password,
                    language: this.checkLanguage(),
                    colorsAccessibility: this.checkAccessibility()
                },
                noNotify: true
            }, true)
                .then(response => {
                    localStorage.removeItem("registerAppId");
                    localStorage.removeItem("maintenanceUser");
                    if (localStorage.getItem("maintenanceStarted") === "true") {
                        localStorage.setItem("maintenanceUser", true);
                    }
                    if (response.data.twoFactorId) {
                        this.goTwoFactorAuth(response, captchaToken);
                    } else if (this.pid) {
                        this.startAppClaimTask();
                    } else {
                        this.checkRedirectAfterLogin();
                        localStorage.setItem("lastUserLogged", this.username.toLowerCase());
                    }
                })
                .catch(err => {
                    this.$resetCaptcha();
                    try {
                        JSON.parse(err.response.data.error);
                        this.userStatusError = true;
                    } catch (e) {
                        if (err.response.data.error.includes("Invalid user username/password")) {
                            this.userInvalidCredentials = this.$t("login.userInvalidCredentials");
                            this.wrongEmailPass = true;
                        } else {
                            this.otherErrorMsg = err.response.data.error;
                            this.otherError = true;
                        }
                    }
                });
        },
        checkRedirectAfterLogin() {
            const taskId = localStorage.getItem("taskId");
            const initialURLCookie = readCookie("initialURL");
            const exportFilename = this.$route.query.fileName;
            const redirectCases = {
                checkInitialUrl: () => {
                    eraseCookie("initialURL");
                    const urlToSend = decodeURIComponent(initialURLCookie);
                    if ((urlToSend.includes("task/?id=") || urlToSend.includes("task?id=")) && !sessionStorage.getItem("isFromTaskId")) {
                        redirectCases.defaultRedirect();
                    } else {
                        window.location.href = urlToSend;
                    }
                },
                sendToTask: () => { window.location.assign(`/task?id=${taskId}`); },
                exportFile: () => {
                    const downloadCases = [
                        { condition: "downloadCsv?fileName",
                            value: `/${PLATFORM_BASE_PATH}/export?fileName=` },
                        { condition: "downloadGDM?fileName",
                            value: `/${PLATFORM_BASE_PATH}/export?type=GDM&fileName=` },
                        { condition: "downloadAuditCSV?fileName",
                            value: `/${PLATFORM_BASE_PATH}/export?type=LOG&fileName=` },
                        { condition: "dsReport?fileName",
                            value: `/${PLATFORM_BASE_PATH}/export?type=DS&fileName=` },
                        { condition: "dsExport?fileName",
                            value: `/${PLATFORM_BASE_PATH}/export?type=DS_EXPORT&fileName=` }
                    ];
                    const downloadType = downloadCases.find(({ condition }) => this.$route.fullPath.includes(condition)
                        || this.$route.redirectedFrom?.includes(condition));
                    let path = "";
                    if (downloadType) {
                        path = `${downloadType.value}${exportFilename}`;
                    }
                    window.location.assign(path);
                },
                createUser: () => {
                    this.getUserWallet(this.inviteUserDetails.email).then(dataWallet => {
                        if (dataWallet.data) {
                            this.$router.push({ name: "changeWalletPassword" });
                        } else {
                            window.location.assign(`/${PLATFORM_BASE_PATH}/userProfile`);
                        }
                    });
                    localStorage.removeItem("createUserId");
                },
                defaultRedirect: () => {
                    const size = window.innerWidth;
                    this.getUserWallet(this.username).then(dataWallet => {
                        if (dataWallet.data) {
                            this.$router.push({ name: "changeWalletPassword" });
                        } else {
                            this.$store.dispatch("tasks/getUserDetails").then(userDetails => {
                                if (userDetails && userDetails.needsTaskRedirect) {
                                    this.$store.dispatch("tasks/getSelfTaskInfo").then(rsp => {
                                        window.location.assign(`/${PLATFORM_BASE_PATH}/tasks/${rsp.taskId || rsp.id}`);
                                    });
                                } else if (size < this.$q.screen.sizes.sm) {
                                    window.location.assign(`/${PLATFORM_BASE_PATH}/tasks`);
                                } else {
                                    window.location.assign(`/${PLATFORM_BASE_PATH}/mydashboard`);
                                    localStorage.removeItem("localIsSmallScreen");
                                }
                            });
                        }
                    });
                }
            };
            this.$resetCaptcha();
            const loginConditions = [
                { condition: initialURLCookie != null, name: "checkInitialUrl" },
                { condition: taskId, name: "sendToTask" },
                { condition: exportFilename, name: "exportFile" },
                { condition: this.createUserId, name: "createUser" },
                { condition: true, name: "defaultRedirect" }
            ];

            const { name } = loginConditions.find(({ condition }) => condition);
            redirectCases[name]();
        },
        goTwoFactorAuth(response, captchaToken) {
            const { data: { twoFactorId, needPhone, skipNumber, phone } = {} } = response;
            const addAttr = () => (needPhone ? { skipNumber } : { phoneNo: phone });
            const name = needPhone ? "phoneNumber" : "twoFactor";
            this.$router.push({
                name,
                params: {
                    ...addAttr(),
                    username: this.username,
                    twoFactorId,
                    captchaToken,
                    language: this.checkLanguage(),
                    colorsAccessibility: this.checkAccessibility(),
                    pid: this.pid,
                    fileName: this.$route.query.fileName
                }
            });
        },
        startAppClaimTask() {
            $http({
                url: `/engine/api/processDefinition/${this.pid}/start`,
                type: "GET",
                noNotify: true
            }, true).then(({ data: dataRsp }) => {
                const data = JSON.parse(JSON.stringify(dataRsp));
                if (data.guiId && data.taskId) {
                    localStorage.removeItem("ifPid");
                    this.$store.dispatch("components/claimTask", data.taskId, { root: true }).then(rsp => {
                        data.canUnclaim = rsp.data.canUnclaim;
                        data.__dashboard = true;
                        window.location.assign(`/${PLATFORM_BASE_PATH}/tasks/${data.taskId}`);
                    });
                } else {
                    sessionStorage.setItem("triggerNoTaskPermission", "noPermisionsToStartApp");
                    window.location.assign(`/${PLATFORM_BASE_PATH}/mydashboard`);
                }
            })
                .catch(() => {
                    sessionStorage.setItem("triggerNoTaskPermission", "noPermisionsToInitiateApp");
                    window.location.assign(`/${PLATFORM_BASE_PATH}/mydashboard`);
                });
        },
        sendResetLink() {
            const url = "/identity/public/resetPassword";

            return new Promise((resolve, reject) => {
                this.$checkCaptcha().then(token => {
                    $http({
                        url, type: "POST", data: { captchaToken: token, username: this.username }, noNotify: true
                    })
                        .then(() => resolve())
                        .catch((err) => reject(err));
                });
            });
        },
        checkEmail() {
            if (this.username) {
                this.wrongEmailPass = false;
                this.otherError = false;
                if (this.$refs && this.$refs.passwordInputRef && this.$refs.passwordInputRef.$refs.AQInput.showPass) {
                    this.$refs.passwordInputRef.$refs.AQInput.togglePass();
                }

                localStorage.removeItem("isLogout");
                $http({
                    url: "/identity/auth/user/check",
                    data: {
                        tenantDomain: "default",
                        username: this.username.toLowerCase(),
                        userPassword: this.password,
                        colorsAccessibility: this.checkAccessibility()
                    },
                    noNotify: true
                }, true)
                    .then(rsp => {
                        if (rsp && rsp.data) {
                            if (typeof rsp.data.hasPassword === "boolean" && !rsp.data.hasPassword && !rsp.data.sourceType) {
                                this.sendResetLink().then(() => {
                                    this.$resetCaptcha();
                                    localStorage.setItem("fromSAML", false);
                                    this.emailState = false;
                                    this.showPassForSAML = true;
                                });
                            } else {
                                const contentType = rsp.headers["content-type"];
                                if (contentType.includes("text/html")) {
                                    localStorage.setItem("lastUserLogged", this.username.toLowerCase());
                                    if (localStorage.getItem("maintenanceStarted") === "true") {
                                        localStorage.setItem("maintenanceUser", true);
                                    }
                                    localStorage.setItem("fromSAML", true);
                                    localStorage.setItem("isUserUsr", false);
                                    localStorage.setItem("accessibilityFeature", this.accessibilityEnabled);
                                    document.write(rsp.data);
                                } else {
                                    localStorage.setItem("fromSAML", false);
                                    this.emailState = false;
                                    const { userType } = rsp.data;
                                    if (userType === "TLDAPUSR" || userType === "TEXTUSR") {
                                        this.hideReset = true;
                                    }
                                }
                            }
                            this.closeAccessibility = true;
                        }
                    })
                    .catch(err => {
                        if (err.response && err.response.data && err.response.data.error === "The platform is under maintenance") {
                            this.notMaintenanceUser = true;
                        }
                    });
            } else {
                this.emailEmpty = true;
            }
        },
        removeUsernameErros() {
            this.emailEmpty = false;
            this.notMaintenanceUser = false;
        },
        removePassErros() {
            this.passEmpty = false;
            this.userStatusError = false;
            this.wrongEmailPass = false;
            this.otherError = false;
            this.passwordsMatchErr = false;
            this.firstNameEmpty = false;
            this.lastNameEmpty = false;
        },
        backToInitial() {
            this.emailState = true;
            this.hideReset = false;
            this.showPassForSAML = false;
            this.closeAccessibility = true;
            this.removePassErros();
        },
        redirectToReset() {
            if (this.pid) {
                localStorage.setItem("ifPid", this.pid);
            }
            this.$router.push({ name: "reset", params: { username: this.username } });
        },
        backToLoginState() {
            this.isLogout = false;
        }
    }

};
